import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { PLAN_LIMITS } from "@currencycore/shared";
import type { InvoiceDTO, OverageInfo, PlanTier, SupportedCountry, UsageStats } from "@currencycore/shared";
import { cancelSubscription, friendlyErrorMessage, getBillingCountries, getBillingPortalUrl, getOrgInvoices, getOrgPlans, getOrgUsage, getOverage, payOverage, resumeSubscription, setBillingRegion, setOrgHardCap } from "../lib/api.ts";
import { toast } from "../components/Toast.tsx";

function fmt(n: number) {
  if (n === Number.POSITIVE_INFINITY) return "Unlimited";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function invoiceMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format((cents ?? 0) / 100);
  } catch {
    return `$${((cents ?? 0) / 100).toFixed(2)}`;
  }
}
function invoiceDate(s: string | null): string {
  if (!s) return "—";
  const iso = s.includes("T") ? s : `${s.replace(" ", "T")}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}
const INVOICE_BADGE: Record<string, string> = {
  paid: "badge-ok",
  refunded: "badge-warn",
  refund_pending: "badge-warn",
  void: "badge-neutral",
  open: "badge-warn",
};

export function BillingPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [currentPlan, setCurrentPlan] = useState<PlanTier>("free");
  // The default "free" above is a fallback only — it must NOT be shown while the
  // real plan/usage are still loading, or the page flashes "Hobby" first. Track
  // each fetch so the plan + usage UI render skeletons until they've resolved.
  const [planLoaded, setPlanLoaded] = useState(false);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [scheduled, setScheduled] = useState<{ plan: string; endsAt: string } | null>(null);
  const [status, setStatus] = useState<string>("active");
  const [renewalCents, setRenewalCents] = useState(0);
  const [overage, setOverage] = useState<OverageInfo | null>(null);
  const [payingOverage, setPayingOverage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [capOn, setCapOn] = useState(false);
  const [capValue, setCapValue] = useState("");
  const [capSaving, setCapSaving] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceDTO[] | null>(null);
  const [countries, setCountries] = useState<SupportedCountry[]>([]);
  const [region, setRegion] = useState("");
  const [regionLoaded, setRegionLoaded] = useState(false);
  const [regionSaving, setRegionSaving] = useState(false);

  // Billing region: supported countries + the org's selection (auto-detected from
  // IP on first load). Owners/admins can change it; it seeds tax/payment country.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getBillingCountries(orgId)
      .then((r) => {
        if (!active) return;
        setCountries(r.countries);
        setRegion(r.selected);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setRegionLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  async function saveRegion(country: string) {
    if (!orgId || country === region) return;
    const prev = region;
    setRegion(country);
    setRegionSaving(true);
    try {
      await setBillingRegion(orgId, country);
      toast("Billing region updated.", true);
    } catch {
      setRegion(prev);
      toast("Couldn't update the billing region.");
    } finally {
      setRegionSaving(false);
    }
  }

  // Over-usage state (pending + owed overage, next-billing from Dodo). Loaded on
  // its own so the Dodo round-trip never blocks the rest of the page.
  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    getOverage(orgId)
      .then((o) => { if (alive) setOverage(o); })
      .catch(() => {});
    return () => { alive = false; };
  }, [orgId]);

  // Load the org's real current plan + any pending cancel-at-period-end.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getOrgPlans(orgId)
      .then((res) => {
        if (!active) return;
        const cur = res.current;
        if (cur.kind === "standard" && cur.planId in PLAN_LIMITS) setCurrentPlan(cur.planId as PlanTier);
        if (cur.scheduledPlan && cur.scheduledAt) setScheduled({ plan: cur.scheduledPlan, endsAt: cur.scheduledAt });
        setStatus(cur.status ?? "active");
        // Next renewal charge: the plan's price for the org's billing interval
        // (falling back to the catalog monthly price), or 0 when free / a
        // cancellation is scheduled. Drives the "Next charge" card.
        const ivl = cur.interval;
        const dbCents = ivl ? cur.prices?.[ivl] : undefined;
        const monthlyCents =
          cur.planId in PLAN_LIMITS
            ? Math.round(Math.max(0, PLAN_LIMITS[cur.planId as PlanTier].priceUsd) * 100)
            : 0;
        setRenewalCents(cur.scheduledPlan ? 0 : dbCents || monthlyCents);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setPlanLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  // Load the org's invoices (Dodo-hosted; empty for free orgs).
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getOrgInvoices(orgId)
      .then((r) => active && setInvoices(r.invoices))
      .catch(() => active && setInvoices([]));
    return () => {
      active = false;
    };
  }, [orgId]);

  // Load usage so we can show the hard-cap card (usage vs. cap, viewer role, ceiling).
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getOrgUsage(orgId)
      .then((u) => {
        if (!active) return;
        setUsage(u);
        setCapOn(u.hardCapRequests != null);
        setCapValue(u.hardCapRequests != null ? String(u.hardCapRequests) : String(u.effectiveLimit ?? ""));
      })
      .catch(() => {})
      .finally(() => {
        if (active) setUsageLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  const canEditCap = usage?.viewerRole === "owner" || usage?.viewerRole === "admin";
  const isHobby = usage?.plan === "free";

  async function saveCap(next: number | null) {
    if (!orgId) return;
    setCapSaving(true);
    setError(null);
    try {
      const res = await setOrgHardCap(orgId, next);
      setUsage((u) =>
        u
          ? {
              ...u,
              hardCapRequests: res.hardCapRequests,
              adminHardCapRequests: res.adminHardCapRequests,
              effectiveCap: res.effectiveCap,
            }
          : u,
      );
      setCapOn(res.hardCapRequests != null);
      toast(next == null ? "Usage limit turned off" : "Usage limit saved", true);
    } catch (err) {
      setError(friendlyErrorMessage(err, "Could not save the usage limit."));
    } finally {
      setCapSaving(false);
    }
  }

  async function openPortal() {
    if (!orgId) return;
    // Open the tab synchronously (within the click gesture) so popup blockers
    // allow it, then point it at the portal once we have the URL — this keeps
    // the user's CurrencyCore tab open so they can come back.
    const tab = window.open("about:blank", "_blank");
    try {
      const { url } = await getBillingPortalUrl(orgId);
      if (!url) throw new Error("no portal url");
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch {
      tab?.close();
      toast("Couldn't open the billing portal. Please try again.");
    }
  }

  async function handleCancel() {
    if (!orgId) return;
    setCancelLoading(true);
    setError(null);
    try {
      const res = await cancelSubscription(orgId);
      setScheduled({ plan: res.plan, endsAt: res.endsAt });
      setCancelOpen(false);
      toast("Subscription cancelled. Your plan stays active until the period ends.", true);
    } catch (err) {
      setError(friendlyErrorMessage(err, "Could not cancel the subscription."));
    } finally {
      setCancelLoading(false);
    }
  }

  async function handleResume() {
    if (!orgId) return;
    setError(null);
    const wasCancellation = scheduled?.plan === "free";
    try {
      await resumeSubscription(orgId);
      setScheduled(null);
      toast(
        wasCancellation
          ? "Cancellation reverted. Your plan continues."
          : "Downgrade cancelled. You're staying on your current plan.",
        true,
      );
    } catch (err) {
      setError(friendlyErrorMessage(err, "Could not reverse the scheduled change."));
    }
  }

  /** Pay the outstanding overage now: redirect to Dodo's link if one is returned,
   *  else it auto-collected from the card on file. */
  async function handlePayOverage() {
    if (!orgId) return;
    setPayingOverage(true);
    try {
      const res = await payOverage(orgId);
      if (res.payUrl) {
        window.location.href = res.payUrl; // complete payment on Dodo's hosted page
        return;
      }
      toast(
        res.pending
          ? `Overage payment of ~$${(res.amountCents / 100).toFixed(2)} is being confirmed with the payment provider. No need to pay again — we'll update your balance once it settles.`
          : `Overage payment started (~$${(res.amountCents / 100).toFixed(2)}). It will settle on your billing shortly.`,
        true,
      );
      const fresh = await getOverage(orgId).catch(() => null);
      if (fresh) setOverage(fresh);
    } catch (err) {
      toast(friendlyErrorMessage(err, "Couldn't start the overage payment."));
    } finally {
      setPayingOverage(false);
    }
  }

  const plan = PLAN_LIMITS[currentPlan];
  // Only render the real plan/price/usage once BOTH fetches have resolved.
  // Until then the values come from the "free" fallback, so we show skeletons.
  const summaryLoaded = planLoaded && usageLoaded;
  // Real usage from the API drives the meter. effectiveLimit === null means an
  // unlimited plan; otherwise compute the used percentage (capped at 100).
  const usageRequests = usage?.requests ?? 0;
  const usageLimit = usage?.effectiveLimit ?? null;
  const unlimited = usageLimit === null;
  const pct = unlimited || usageLimit === 0 ? 0 : Math.min(100, Math.round((usageRequests / usageLimit) * 100));
  const meterClass = pct >= 90 ? "danger" : pct >= 70 ? "warn" : "";

  return (
    <div className="animate-fade-in">
      {/* Page header */}
      <div className="page-head rise">
        <div>
          <h1>Billing</h1>
          <p className="sub">Manage your plan and invoices.</p>
        </div>
      </div>

      {error && (
        <div className="error-banner" style={{ marginBottom: "18px" }}>{error}</div>
      )}

      {status === "past_due" && (
        <div className="error-banner" style={{ marginBottom: "18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <span>
            Your account is <strong>on hold</strong>. Billing is past due, so live API keys are paused until payment is cleared.
            {!canEditCap && " An owner or admin needs to update the payment method."}
          </span>
          {canEditCap && <button className="btn btn-sm" onClick={() => void openPortal()}>Update payment</button>}
        </div>
      )}

      {!canEditCap && (
        <div className="card card-pad" style={{ marginBottom: "18px", fontSize: "13px" }}>
          You have <strong>view-only</strong> billing access. Only owners and admins can change the plan, buy credits, or update payment.
        </div>
      )}

      {scheduled && (
        <div
          style={{
            marginBottom: "18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
            padding: "12px 16px",
            borderRadius: "var(--radius)",
            border: "1px solid oklch(75% 0.15 75 / 0.4)",
            background: "oklch(75% 0.15 75 / 0.1)",
            fontSize: "13px",
          }}
        >
          <span>
            {scheduled.plan === "free" ? (
              <>
                Your plan switches to <strong>Hobby</strong> on{" "}
                <strong>{new Date(scheduled.endsAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</strong>.
                {" "}Live keys stop working then. Test keys keep working at the Hobby limit.
              </>
            ) : (
              <>
                Your plan switches to{" "}
                <strong>{PLAN_LIMITS[scheduled.plan as PlanTier]?.label ?? scheduled.plan}</strong> on{" "}
                <strong>{new Date(scheduled.endsAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</strong>.
                {" "}You keep your current plan until then.
              </>
            )}
          </span>
          <button className="btn btn-sm" onClick={handleResume}>Keep my plan</button>
        </div>
      )}

      {/* Summary split */}
      {!summaryLoaded ? (
        <div className="split rise">
          {/* Current plan card skeleton — occupies the same space as the real
              card so the real plan label/price/usage don't flash a default. */}
          <div className="card card-pad">
            <div className="between" style={{ marginBottom: "18px" }}>
              <div className="skeleton" style={{ height: "12px", width: "90px" }} />
              <div className="skeleton" style={{ height: "20px", width: "70px", borderRadius: "99px" }} />
            </div>
            <div className="between" style={{ alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div className="skeleton" style={{ height: "30px", width: "140px" }} />
                <div className="skeleton" style={{ height: "13px", width: "200px", marginTop: "8px" }} />
              </div>
              <div className="skeleton" style={{ height: "30px", width: "90px" }} />
            </div>
            <hr className="divider" style={{ margin: "18px 0" }} />
            <div className="between" style={{ marginBottom: "10px" }}>
              <div className="skeleton" style={{ height: "13px", width: "150px" }} />
              <div className="skeleton" style={{ height: "13px", width: "100px" }} />
            </div>
            <div className="skeleton" style={{ height: "8px", borderRadius: "99px" }} />
            <div className="skeleton" style={{ height: "12px", width: "80%", marginTop: "12px" }} />
          </div>

          {/* Side: this period skeleton */}
          <div className="stack" style={{ gap: "16px" }}>
            <div className="card card-pad">
              <div className="skeleton" style={{ height: "12px", width: "90px", marginBottom: "12px" }} />
              <div className="skeleton" style={{ height: "26px", width: "110px" }} />
              <div className="skeleton" style={{ height: "12.5px", width: "140px", marginTop: "8px" }} />
              <div className="skeleton" style={{ height: "34px", marginTop: "16px" }} />
            </div>
          </div>
        </div>
      ) : (
      <div className="split rise">
        {/* Current plan card */}
        <div className="card card-pad">
          <div className="between" style={{ marginBottom: "18px" }}>
            <div className="eyebrow">Current plan</div>
            <span className="badge badge-ok"><span className="dot" />Active</span>
          </div>
          <div className="between" style={{ alignItems: "flex-end" }}>
            <div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: "28px", fontWeight: 600 }}>{plan.label}</div>
              <div className="muted" style={{ fontSize: "13px" }}>
                {fmt(plan.monthlyRequests)} requests / month · renews next period
              </div>
            </div>
            <div className="tar">
              <div style={{ fontFamily: "var(--font-display)", fontSize: "28px", fontWeight: 600 }}>
                {plan.priceUsd === 0 ? "$0" : plan.priceUsd === -1 ? "Custom" : `$${plan.priceUsd}`}
                <small className="muted" style={{ fontSize: "13px", fontWeight: 400 }}>/mo</small>
              </div>
            </div>
          </div>
          <hr className="divider" style={{ margin: "18px 0" }} />
          <div className="between" style={{ marginBottom: "10px" }}>
            <span className="muted" style={{ fontSize: "13px" }}>Included requests used</span>
            <strong className="tnum" style={{ fontSize: "13px" }}>
              {usageRequests.toLocaleString()} / {unlimited ? "Unlimited" : usageLimit.toLocaleString()}
            </strong>
          </div>
          <div className={`meter${meterClass ? ` ${meterClass}` : ""}`}>
            <span style={{ width: `${pct}%` }} />
          </div>
          {isHobby ? (
            <p className="muted" style={{ fontSize: "12px", marginTop: "10px" }}>
              Overage is blocked on Hobby.{" "}
              <Link to="../plans" style={{ color: "var(--accent-text)" }}>View plans</Link> to upgrade for more requests and metered overage.
            </p>
          ) : plan.overagePer1000Cents != null ? (
            <p className="muted" style={{ fontSize: "12px", marginTop: "10px" }}>
              Usage beyond your included requests is billed at{" "}
              <strong>${(plan.overagePer1000Cents / 100).toFixed(2)} / 1,000 requests</strong> (metered overage).
            </p>
          ) : (
            <p className="muted" style={{ fontSize: "12px", marginTop: "10px" }}>
              Overage is billed per your plan's custom terms.{" "}
              <Link to="../plans" style={{ color: "var(--accent-text)" }}>View plans</Link>.
            </p>
          )}
          {overage && overage.available && (overage.pendingCents > 0 || overage.owedCents > 0) && (
            <div
              style={{ marginTop: "12px", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: "var(--radius-xs)" }}
            >
              <div className="between">
                <span className="muted" style={{ fontSize: "13px" }}>Overage this period</span>
                <strong className="tnum" style={{ fontSize: "13px" }}>
                  ${((overage.pendingCents + overage.owedCents) / 100).toFixed(2)}
                </strong>
              </div>
              <p className="muted" style={{ fontSize: "12px", margin: "6px 0 0", lineHeight: 1.5 }}>
                {overage.billedHow === "renewal" ? (
                  <>
                    Metered usage past your included requests. It's added to your next invoice
                    {overage.nextBillingDate ? ` on ${new Date(overage.nextBillingDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}` : ""}
                    {" "}and charged to your card on file, with the rest of your plan.
                  </>
                ) : (
                  <>
                    On a multi-month plan, overage is charged to your card on file <strong>each month</strong>
                    {overage.nextBillingDate ? ` (renewal ${new Date(overage.nextBillingDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })})` : ""}.
                    {" "}If a charge can't be auto-collected, we email the owner a payment link, or you can pay it here.
                  </>
                )}
              </p>
              {overage.owedCents > 0 && canEditCap && (
                <button
                  className="btn btn-primary btn-sm"
                  style={{ marginTop: "10px" }}
                  disabled={payingOverage}
                  onClick={() => void handlePayOverage()}
                >
                  {payingOverage ? <><span className="spinner" />Starting…</> : `Pay ${"$"}${(overage.owedCents / 100).toFixed(2)} now`}
                </button>
              )}
              {overage.owedCents > 0 && !canEditCap && (
                <p className="muted" style={{ fontSize: "12px", margin: "8px 0 0" }}>
                  An owner or admin can pay this from the app.
                </p>
              )}
            </div>
          )}
          {currentPlan !== "free" && !scheduled && canEditCap && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginTop: "14px" }}
              onClick={() => setCancelOpen(true)}
            >
              Cancel subscription
            </button>
          )}
        </div>

        {/* Side: this period */}
        <div className="stack" style={{ gap: "16px" }}>
          <div className="card card-pad">
            <div className="eyebrow" style={{ marginBottom: "12px" }}>Next charge</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "26px", fontWeight: 600 }}>
              {plan.priceUsd === -1 ? "Custom" : invoiceMoney(renewalCents, "USD")}
            </div>
            <div className="muted" style={{ fontSize: "12.5px" }}>
              {currentPlan === "free"
                ? "Hobby is free, no charge"
                : scheduled
                  ? "switches to Hobby, no charge"
                  : "due at your next billing date"}
            </div>
            {canEditCap && currentPlan !== "free" && (
              <button
                className="btn btn-ghost mt"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => void openPortal()}
              >
                Manage payment method
              </button>
            )}
            <Link
              className="btn btn-primary mt"
              style={{ width: "100%", justifyContent: "center" }}
              to="../plans"
            >
              {currentPlan === "free" ? "Upgrade plan" : "Change plan"}
            </Link>
          </div>
          <div className="card card-pad">
            <div className="eyebrow" style={{ marginBottom: "12px" }}>Billing region</div>
            {regionLoaded ? (
              <>
                <select
                  className="input"
                  value={region}
                  disabled={!canEditCap || regionSaving}
                  onChange={(e) => void saveRegion(e.target.value)}
                  aria-label="Billing region"
                >
                  {region && !countries.some((c) => c.code === region) && (
                    <option value={region}>{region}</option>
                  )}
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
                <div className="muted" style={{ fontSize: "12px", marginTop: "8px" }}>
                  {canEditCap
                    ? "Sets the country used for tax and payment on your invoices."
                    : "Set by an owner or admin."}
                </div>
              </>
            ) : (
              <div className="skeleton" style={{ height: "38px" }} />
            )}
          </div>
        </div>
      </div>
      )}

      {/* Usage limit (hard cap) */}
      {usage && (
        <div className="card card-pad mt rise">
          <div className="between">
            <strong style={{ fontSize: "14px" }}>Usage limit (hard cap)</strong>
            {isHobby ? (
              <span className="badge badge-neutral">Not available on Hobby</span>
            ) : (
              <label className="switch">
                <input
                  type="checkbox"
                  checked={capOn}
                  disabled={!canEditCap || capSaving}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setCapOn(true);
                      setCapValue(String(usage.effectiveLimit ?? ""));
                    } else {
                      void saveCap(null);
                    }
                  }}
                />
                <span className="track" />
                <span className="thumb" />
              </label>
            )}
          </div>

          {!isHobby && capOn && (
            <div className="field" style={{ marginTop: "12px" }}>
              <label className="form-label">Block requests beyond</label>
              <input
                className="input"
                inputMode="numeric"
                value={capValue}
                disabled={!canEditCap}
                onChange={(e) => setCapValue(e.target.value.replace(/[^\d]/g, ""))}
              />
              <p className="muted" style={{ fontSize: "12px", marginTop: "8px" }}>
                This period: {usage.requests.toLocaleString()} /{" "}
                {capValue ? Number(capValue).toLocaleString() : "—"} requests. Beyond the cap, requests are
                blocked (429), not billed as overage.
              </p>
              {usage.adminHardCapRequests != null && (
                <p className="muted" style={{ fontSize: "12px" }}>
                  Admin ceiling: {usage.adminHardCapRequests.toLocaleString()}. You can’t set above this.
                </p>
              )}
              <div className="ban-row" style={{ marginTop: "10px" }}>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!canEditCap || capSaving || !capValue}
                  onClick={() => {
                    const n = Number(capValue);
                    if (usage.adminHardCapRequests != null && n > usage.adminHardCapRequests) {
                      setError(
                        `Cap can’t exceed the admin ceiling of ${usage.adminHardCapRequests.toLocaleString()}.`,
                      );
                      return;
                    }
                    void saveCap(n);
                  }}
                >
                  {capSaving ? <span className="spinner" /> : "Save limit"}
                </button>
              </div>
            </div>
          )}

          {!isHobby && !canEditCap && (
            <p className="muted" style={{ fontSize: "12px", marginTop: "8px" }}>
              Only owners and admins can change this.
            </p>
          )}
        </div>
      )}

      {/* Invoices — Dodo-hosted (official Merchant-of-Record documents). */}
      <div className="card mt-lg rise">
        <div className="card-head">
          <h2>Invoice history</h2>
        </div>
        {invoices === null ? (
          <div className="skeleton" style={{ height: "120px" }} />
        ) : invoices.length === 0 ? (
          <p className="muted" style={{ padding: "4px 2px" }}>
            No invoices yet. Paid invoices appear here after your first payment.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="col-num">Amount</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td className="mono strong">{inv.id}</td>
                    <td className="muted">{invoiceDate(inv.date)}</td>
                    <td className="muted">{inv.description ?? "—"}</td>
                    <td className="col-num strong">{invoiceMoney(inv.amountCents, inv.currency)}</td>
                    <td>
                      <span className={`badge ${INVOICE_BADGE[inv.status] ?? "badge-neutral"}`}>
                        {inv.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="col-right">
                      {inv.hostedUrl ? (
                        <a className="btn btn-sm btn-ghost" href={inv.hostedUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Invoice
                        </a>
                      ) : (
                        <span className="muted" style={{ fontSize: "12px" }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cancel confirmation */}
      {cancelOpen && createPortal(
        <div
          className="modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget && !cancelLoading) setCancelOpen(false); }}
        >
          <div className="modal-box animate-fade-in">
            <h2>Cancel subscription?</h2>
            <p>
              You keep <strong>{PLAN_LIMITS[currentPlan].label}</strong> and full access until the end of your
              current billing period. After that your organization moves to <strong>Hobby</strong>: live API
              keys stop working (test keys keep working at the Hobby limit) and overage billing ends. You can
              reverse this any time before then.
            </p>
            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setCancelOpen(false)} disabled={cancelLoading}>Keep plan</button>
              <button className="btn btn-primary" onClick={handleCancel} disabled={cancelLoading}>
                {cancelLoading ? <><span className="spinner" />Cancelling…</> : "Cancel at period end"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
